import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  Observable,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { Business } from '../../../store/business/business.state'
import { Basis, PayoutFrequency } from '../../../store/payout-agreement/payout-agreement.state'
import { select, Store } from '@ngrx/store'
import { loadBusinesses } from '../../../store/business/business.actions'
import {
  contractorPayrollBatchesActions,
  runningCPActions
} from '../../../store/contractor-payroll/contractor-payroll.actions'
import {
  ContractorPayrollStatus,
  IncomingPayrollBatch,
  IncomingRunningPayrollBatch
} from '../../../store/contractor-payroll/contractor-payroll.state'
import {
  selectPayrollBatches,
  selectRunningPayroll
} from '../../../store/contractor-payroll/contractor-payroll.reducers'
import { CommonModule, NgFor, NgIf, TitleCasePipe } from '@angular/common'
import { CapitalizePipe } from '../../../pipes/capitalize.pipe'
import { selectBusinesses } from '../../../store/business/business.reducer'
import { eps } from '../../../services/url.service'
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Router } from '@angular/router'
import { selectUser } from '../../../store/auth/auth.state'
import { ToastrService } from 'ngx-toastr'
import Papa from 'papaparse'
import dayjs from 'dayjs'

export interface PlaceholderBatch extends IncomingPayrollBatch {
  isPlaceholder: true
}

enum PaymentMode {
  CASH = 'cash',
  CHEQUE = 'cheque',
  NEFT = 'neft',
  RTGS = 'rtgs',
  IMPS = 'imps',
  UPI = 'upi'
}

@Component({
  selector: 'app-manage-contractor-payroll',
  imports: [NgIf, NgFor, CommonModule, ReactiveFormsModule, CapitalizePipe, TitleCasePipe],
  templateUrl: './manage-contractor-payroll.component.html',
  styleUrl: './manage-contractor-payroll.component.scss'
})
export class ManageContractorPayrollComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal)
  private router = inject(Router)
  private toastr = inject(ToastrService)
  @ViewChild('finalizePayrollModal', { static: true })
  finalizePayrollModal!: TemplateRef<NgbActiveModal>
  eps = eps
  PayrollStatus = ContractorPayrollStatus
  PayoutFrequency = PayoutFrequency
  payrollForm!: FormGroup
  businessesList$: Observable<Business[]>
  payrollPaymentDetailsForm!: FormGroup
  payoutFrequencies = Object.values(PayoutFrequency)
  selectedPayrollBatchId = ''

  years: string[] = []
  months: { value: number; name: string }[] = []

  showYearSelect = false
  showMonthYearSelect = false
  isExporting = false
  exportingBatchId: string | null = null

  //payoutAgreements$: Observable<PayoutAgreement[]>
  contractorPayrollBatches$: Observable<IncomingPayrollBatch[] | null>
  displayBatches$!: Observable<(IncomingPayrollBatch | PlaceholderBatch)[]>
  private destroy$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
    private store: Store
  ) {
    this.businessesList$ = this.store.pipe(select(selectBusinesses))
    //this.payoutAgreements$ = this.store.pipe(select(selectPayoutAgreements))
    this.contractorPayrollBatches$ = this.store.pipe(select(selectPayrollBatches))
    this.generateYearsAndMonths()
  }

  private getCalendarYearForMonth(financialYear: string, month: number): number {
    const [startYear] = financialYear.split('-').map(Number)
    // Months 1-3 (Jan-Mar) belong to the second year of the FY
    return month >= 1 && month <= 3 ? startYear + 1 : startYear
  }

  ngOnInit(): void {
    this.initForm()
    this.loadBusinessesList()
    this.setupFormListeners()
    this.initializePaymentDetailsForm()
  }

  private initForm(): void {
    const today = new Date()
    const currentMonth = today.getMonth() + 1 // 1-12
    const currentCalendarYear = today.getFullYear()
    const currentFinancialYear =
      currentMonth >= 4
        ? `${currentCalendarYear}-${currentCalendarYear + 1}`
        : `${currentCalendarYear - 1}-${currentCalendarYear}`

    this.payrollForm = this.fb.group({
      selectedBusinessId: new FormControl(null),
      selectedPayoutFrequency: new FormControl(PayoutFrequency.BI_WEEKLY),
      selectedYear: new FormControl(currentFinancialYear),
      selectedMonth: new FormControl(today.getMonth() + 1) // Jan is 1
    })
  }

  private initializePaymentDetailsForm(): void {
    this.payrollPaymentDetailsForm = this.fb.group({
      dateOfPayment: [new Date().toISOString().slice(0, 10), Validators.required],
      modeOfPayment: [PaymentMode.NEFT, Validators.required]
    })
  }

  private loadBusinessesList(): void {
    this.store.dispatch(loadBusinesses({}))

    this.businessesList$
      .pipe(
        filter(businesses => !!businesses && businesses.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(businesses => {
        if (businesses.length === 1) {
          this.payrollForm.get('selectedBusinessId')?.setValue(businesses[0].id)
        }
      })
  }

  private setupFormListeners(): void {
    const businessId$ = this.payrollForm
      .get('selectedBusinessId')
      ?.valueChanges.pipe(
        startWith(this.payrollForm.get('selectedBusinessId')?.value),
        distinctUntilChanged()
      ) as Observable<string>

    const payoutFrequency$ = this.payrollForm.get('selectedPayoutFrequency')?.valueChanges.pipe(
      startWith(this.payrollForm.get('selectedPayoutFrequency')?.value),
      distinctUntilChanged(),
      tap((frequency: PayoutFrequency) => {
        this.updateDateSelectors(frequency)
      })
    ) as Observable<PayoutFrequency>

    const selectedYear$ = this.payrollForm
      .get('selectedYear')
      ?.valueChanges.pipe(
        startWith(this.payrollForm.get('selectedYear')?.value),
        distinctUntilChanged()
      ) as Observable<string>
    const selectedMonth$ = this.payrollForm
      .get('selectedMonth')
      ?.valueChanges.pipe(
        startWith(this.payrollForm.get('selectedMonth')?.value),
        distinctUntilChanged()
      ) as Observable<number>

    this.updateDateSelectors(this.payrollForm.get('selectedPayoutFrequency')?.value)

    // Combine relevant form controls to trigger data loading
    this.displayBatches$ = combineLatest([
      businessId$,
      payoutFrequency$,
      selectedYear$,
      selectedMonth$
    ]).pipe(
      filter(([businessId, frequency, year, month]) => {
        const isYearOnlyFrequency = [
          PayoutFrequency.ANNUALLY,
          PayoutFrequency.SEMI_ANNUALLY,
          PayoutFrequency.QUARTERLY,
          PayoutFrequency.MONTHLY
        ].includes(frequency)

        const isMonthAndYearFrequency = [
          PayoutFrequency.BI_WEEKLY,
          PayoutFrequency.WEEKLY,
          PayoutFrequency.DAILY
        ].includes(frequency)

        const hasAllRequiredSelections =
          !!businessId &&
          !!frequency &&
          !!year &&
          (isYearOnlyFrequency || (isMonthAndYearFrequency && !!month))
        return hasAllRequiredSelections
      }),
      switchMap(([businessId, frequency, financialYear, month]) => {
        const { payPeriodStart, payPeriodEnd } = this.getPayPeriodDatesForFetch(
          frequency,
          financialYear,
          month
        )
        this.store.dispatch(
          contractorPayrollBatchesActions.loadBatches({
            belongsTo: businessId,
            payoutFrequency: frequency,
            payPeriodStart: payPeriodStart.toISOString(),
            payPeriodEnd: payPeriodEnd.toISOString()
          })
        )
        return this.contractorPayrollBatches$.pipe(
          map(apiBatches => {
            // generateDisplayBatches will do the heavy lifting
            return this.generateDisplayBatches(
              businessId!,
              frequency,
              financialYear,
              month,
              apiBatches || [] // Ensure it's an array
            )
          })
        )
      }),
      takeUntil(this.destroy$)
    )
  }

  private generateDisplayBatches(
    businessId: string,
    frequency: PayoutFrequency,
    financialYear: string,
    month: number | null,
    apiBatches: IncomingPayrollBatch[]
  ): (IncomingPayrollBatch | PlaceholderBatch)[] {
    const today = new Date()
    // Don't generate future placeholders to keep the UI clean
    today.setHours(23, 59, 59, 999)

    let potentialPeriods: { start: Date; end: Date }[] = []
    const [startYear, endYear] = financialYear.split('-').map(Number)

    switch (frequency) {
      case PayoutFrequency.ANNUALLY:
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 3, 1)), // April 1st
          end: new Date(Date.UTC(endYear, 2, 31)) // March 31st
        })
        break

      case PayoutFrequency.SEMI_ANNUALLY:
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 3, 1)),
          end: new Date(Date.UTC(startYear, 8, 30))
        }) // H1: Apr-Sep
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 9, 1)),
          end: new Date(Date.UTC(endYear, 2, 31))
        }) // H2: Oct-Mar
        break

      case PayoutFrequency.QUARTERLY:
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 3, 1)),
          end: new Date(Date.UTC(startYear, 5, 30))
        }) // Q1: Apr-Jun
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 6, 1)),
          end: new Date(Date.UTC(startYear, 8, 30))
        }) // Q2: Jul-Sep
        potentialPeriods.push({
          start: new Date(Date.UTC(startYear, 9, 1)),
          end: new Date(Date.UTC(startYear, 11, 31))
        }) // Q3: Oct-Dec
        potentialPeriods.push({
          start: new Date(Date.UTC(endYear, 0, 1)),
          end: new Date(Date.UTC(endYear, 2, 31))
        }) // Q4: Jan-Mar
        break

      case PayoutFrequency.MONTHLY:
        for (let i = 0; i < 12; i++) {
          const currentMonth = (3 + i) % 12 // Start from April (month 3)
          const currentYear = i < 9 ? startYear : endYear
          potentialPeriods.push({
            start: new Date(Date.UTC(currentYear, currentMonth, 1)),
            end: new Date(Date.UTC(currentYear, currentMonth + 1, 0)) // Last day of month
          })
        }
        break

      case PayoutFrequency.BI_WEEKLY:
        if (month) {
          const calendarYear = this.getCalendarYearForMonth(financialYear, month)
          const lastDay = new Date(Date.UTC(calendarYear, month, 0)).getUTCDate()
          potentialPeriods.push({
            start: new Date(Date.UTC(calendarYear, month - 1, 16)),
            end: new Date(Date.UTC(calendarYear, month - 1, lastDay))
          })
          potentialPeriods.push({
            start: new Date(Date.UTC(calendarYear, month - 1, 1)),
            end: new Date(Date.UTC(calendarYear, month - 1, 15))
          })
        }
        break

      case PayoutFrequency.WEEKLY:
        if (month) {
          const calendarYear = this.getCalendarYearForMonth(financialYear, month)
          potentialPeriods = this.getWeeklyPeriodsForMonth(calendarYear, month)
        }
        break

      case PayoutFrequency.DAILY:
        if (month) {
          const calendarYear = this.getCalendarYearForMonth(financialYear, month)
          const daysInMonth = new Date(Date.UTC(calendarYear, month, 0)).getUTCDate()
          for (let day = 1; day <= daysInMonth; day++) {
            potentialPeriods.push({
              start: new Date(Date.UTC(calendarYear, month - 1, day)),
              end: new Date(Date.UTC(calendarYear, month - 1, day))
            })
          }
        }
        break
    }

    // Filter out periods that are entirely in the future
    potentialPeriods = potentialPeriods.filter(p => p.start <= today)

    // Merge API data with potential periods
    const allBatches = potentialPeriods.map(period => {
      const existingBatch = apiBatches.find(
        b => new Date(b.payPeriodStart).getTime() === period.start.getTime()
      )

      if (existingBatch) {
        return existingBatch
      } else {
        // Create a placeholder
        const placeholder: PlaceholderBatch = {
          isPlaceholder: true,
          id: `placeholder_${period.start.toISOString()}`,
          belongsTo: businessId,
          payPeriodStart: period.start.toISOString(),
          payPeriodEnd: period.end.toISOString(),
          totalAmount: 0,
          netAmount: 0,
          totalTds: 0,
          contractorCount: 0,
          status: ContractorPayrollStatus.PENDING,
          payoutFrequency: frequency,
          paidBy: null,
          payoutPaidByUser: null,
          dateOfPayment: null,
          modeOfPayment: null,
          processedBy: null,
          payoutProcessedByUser: null,
          processedOn: null,
          createdAt: '',
          updatedAt: ''
        }
        return placeholder
      }
    })

    return allBatches.reverse()
  }

  /**
   * Helper to calculate weekly periods within a given month.
   */
  private getWeeklyPeriodsForMonth(year: number, month: number): { start: Date; end: Date }[] {
    const periods = []
    const date = new Date(Date.UTC(year, month - 1, 1))
    while (date.getUTCMonth() === month - 1) {
      const start = new Date(date)
      const end = new Date(start)
      end.setUTCDate(end.getUTCDate() + 6)

      if (end.getUTCMonth() !== month - 1) {
        // Last day of current month in UTC
        end.setUTCDate(new Date(Date.UTC(year, month, 0)).getUTCDate())
      }

      periods.push({ start, end })
      date.setUTCDate(date.getUTCDate() + 7)
    }
    return periods
  }

  /**
   * Gets a broad date range for fetching all potential batches for a selection.
   */
  private getPayPeriodDatesForFetch(
    frequency: PayoutFrequency,
    financialYear: string,
    month?: number | null
  ): { payPeriodStart: Date; payPeriodEnd: Date } {
    const [startYear, endYear] = financialYear.split('-').map(Number)

    if (
      [PayoutFrequency.BI_WEEKLY, PayoutFrequency.WEEKLY, PayoutFrequency.DAILY].includes(
        frequency
      ) &&
      month
    ) {
      // For frequencies within a month, fetch for the whole month
      const calendarYear = this.getCalendarYearForMonth(financialYear, month)
      return {
        payPeriodStart: new Date(Date.UTC(calendarYear, month - 1, 1)),
        payPeriodEnd: new Date(Date.UTC(calendarYear, month, 0, 23, 59, 59, 999))
      }
    } else {
      // For frequencies across a year, fetch for the whole year
      return {
        payPeriodStart: new Date(Date.UTC(startYear, 3, 1)), // April 1st of start year
        payPeriodEnd: new Date(Date.UTC(endYear, 2, 31, 23, 59, 59, 999)) // March 31st of end year
      }
    }
  }

  private updateDateSelectors(frequency: PayoutFrequency): void {
    const annualFrequencies = [
      PayoutFrequency.ANNUALLY,
      PayoutFrequency.SEMI_ANNUALLY,
      PayoutFrequency.QUARTERLY,
      PayoutFrequency.MONTHLY
    ]
    const monthlyFrequencies = [
      PayoutFrequency.BI_WEEKLY,
      PayoutFrequency.WEEKLY,
      PayoutFrequency.DAILY
    ]

    this.showYearSelect =
      annualFrequencies.includes(frequency) || monthlyFrequencies.includes(frequency)
    this.showMonthYearSelect = monthlyFrequencies.includes(frequency)

    // Reset month if not needed
    const selectedMonthControl = this.payrollForm.get('selectedMonth')
    if (this.showMonthYearSelect) {
      if (selectedMonthControl?.value === null || selectedMonthControl?.value === '') {
        selectedMonthControl?.setValue(new Date().getMonth() + 1, { emitEvent: false })
      }
    } else {
      if (selectedMonthControl?.value !== null) {
        selectedMonthControl?.setValue(null, { emitEvent: false })
      }
    }

    // Ensure year always has a value
    const selectedYearControl = this.payrollForm.get('selectedYear')
    if (selectedYearControl?.value === null || selectedYearControl?.value === '') {
      selectedYearControl?.setValue(new Date().getFullYear(), { emitEvent: false })
    }
  }

  private generateYearsAndMonths(): void {
    const today = new Date()
    let currentYear = today.getFullYear()
    const currentMonth = today.getMonth() // 0-11

    // If the current month is before April, the financial year started last year.
    if (currentMonth < 3) {
      currentYear--
    }

    const financialYears = []
    for (let i = 0; i < 5; i++) {
      const startYear = currentYear - i
      financialYears.push(`${startYear}-${startYear + 1}`)
    }
    this.years = financialYears

    this.months = [
      { value: 1, name: 'January' },
      { value: 2, name: 'February' },
      { value: 3, name: 'March' },
      { value: 4, name: 'April' },
      { value: 5, name: 'May' },
      { value: 6, name: 'June' },
      { value: 7, name: 'July' },
      { value: 8, name: 'August' },
      { value: 9, name: 'September' },
      { value: 10, name: 'October' },
      { value: 11, name: 'November' },
      { value: 12, name: 'December' }
    ]
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  setupRunningPayroll(batchData: IncomingPayrollBatch, finalize = false): void {
    if (batchData.status === ContractorPayrollStatus.PROCESSED && finalize) {
      this.openFinalizePayrollModal(this.finalizePayrollModal, batchData.id)
    } else {
      this.store.dispatch(
        runningCPActions.setupRunningPayroll({
          batchData: {
            ...batchData,
            contractorPayrolls: {
              fixed: [],
              hourly: []
            },
            processedBy: null,
            paidBy: null
          }
        })
      )
      this.router.navigate([eps.hrm.runContractorPayrollPath])
    }
  }

  private openFinalizePayrollModal(modal: unknown, batchId: string) {
    this.selectedPayrollBatchId = batchId
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'md'
    })
  }

  finalizePayroll(batchId: string) {
    const data = this.payrollPaymentDetailsForm.value
    data.batchId = batchId
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        if (!user) return
        data.paidBy = user.id
      })
    this.store.dispatch(contractorPayrollBatchesActions.finalizePayroll(data))
    this.onClose()
  }

  openDeleteModal(modal: unknown, batchId: string) {
    this.selectedPayrollBatchId = batchId
    this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'sm'
    })
  }

  deletePayrollBatch(batchId: string) {
    this.store.dispatch(contractorPayrollBatchesActions.deletePayrollBatch({ id: batchId }))
    this.onClose()
  }

  onClose() {
    this.selectedPayrollBatchId = ''
    this.modalService.dismissAll()
  }

  async exportPayrollToCsv(batchData: IncomingPayrollBatch): Promise<void> {
    // 1. Guard against trying to export a placeholder batch
    if (!batchData.id) {
      this.toastr.error("This month's contractor payroll has not been generated yet.")
      return
    }

    const payStartDate = dayjs(batchData.payPeriodStart).format('YYYY-MM-DD')
    const payEndDate = dayjs(batchData.payPeriodEnd).format('YYYY-MM-DD')

    // 2. Set loading state for better UX
    this.isExporting = true
    this.exportingBatchId = batchData.id

    try {
      // 3. Dispatch action to fetch detailed payroll data including employees
      this.store.dispatch(
        runningCPActions.loadRunningPayroll({
          payPeriodStart: batchData.payPeriodStart,
          payPeriodEnd: batchData.payPeriodEnd,
          belongsTo: batchData.belongsTo,
          payoutFrequency: batchData.payoutFrequency
        })
      )

      // 4. Wait for the detailed data to be loaded into the store
      const fullPayrollBatch = await firstValueFrom(
        this.store.pipe(
          select(selectRunningPayroll),
          // Filter to ensure we have the data and it's for the batch we requested
          filter(
            data =>
              !!data &&
              data.id === batchData.id &&
              data.contractorPayrolls.fixed.length > 0 &&
              data.contractorPayrolls.hourly.length > 0
          )
        )
      )

      if (!fullPayrollBatch) {
        return
      }

      // 5. Prepare the data for CSV conversion
      const payrolls = [
        ...fullPayrollBatch.contractorPayrolls.fixed,
        ...fullPayrollBatch.contractorPayrolls.hourly
      ]
      const csvData = payrolls.map(payroll => ({
        'Pay Period Start Date': payStartDate,
        'Pay Period End Date': payEndDate,
        'Employee Name': `${payroll.employee.firstName} ${payroll.employee.lastName}`,
        'Employee ID': payroll.employee.empId,
        Basis: payroll.basis,
        'Gross Payout': payroll.grossPayout,
        'Net Payout': payroll.netPayout,
        TDS: payroll.tds,
        'Work Hours': payroll.totalHours,
        'LOP Days': payroll.lopDays,
        Bonus: payroll.bonus,
        Expenses: payroll.expenses,
        Adjustments: payroll.adjustments,
        Rounding: payroll.rounding
      }))

      // 6. Use PapaParse to convert JSON to a CSV string
      const csvString = Papa.unparse(csvData)

      // 7. Trigger the browser download
      this.downloadCsv(csvString, fullPayrollBatch)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this.toastr.error('Failed to export contractor payroll data. Please try again.')
    } finally {
      // 8. Reset loading state
      this.isExporting = false
      this.exportingBatchId = null
    }
  }

  /**
   * A helper utility to trigger the download of the generated CSV string.
   * @param csvString The CSV content as a string.
   * @param batch The payroll batch used for generating a filename.
   */
  private downloadCsv(csvString: string, batch: IncomingRunningPayrollBatch): void {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })

    // Generate a descriptive filename
    const date = new Date(batch.payPeriodStart)
    const monthName = date.toLocaleString('default', { month: 'long' })
    const year = date.getFullYear()
    const fileName = `Contractor-Payroll-${monthName}-${year}.csv`

    // Create a temporary link to trigger the download
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
